import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Card from '@mui/material/Card';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';
import { styled } from '@mui/material/styles';
import { toast } from 'react-toastify';
import api from '../services/api';
import Chip from '@mui/material/Chip';
import { Search, X, Filter, Eye, Building2 as IssuerLogoIcon } from 'lucide-react';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Badge from '@mui/material/Badge';
import FilterAltIcon from '@mui/icons-material/FilterAlt';

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
}));

export default function Issuer() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('id');
  const [filterSearch, setFilterSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const totalNegotiations = list.reduce(
    (sum, item) => sum + (item.total_negotiations || 0),
    0
  );

  const fetchData = async () => {
    try {
      const res = await api.get('/issuer/with-totals');
      setList(res.data);
    } catch (error) {
      console.error('Error fetching issuer list:', error);
      toast.error('Errore nel caricamento dei creditori');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterSearch = (event) => {
    setFilterSearch(event.target.value);
    setPage(0);
  };

  const handleFilterType = (event) => {
    setFilterType(event.target.value);
    setPage(0);
  };

  const clearFilters = () => {
    setFilterSearch('');
    setFilterType('');
    setPage(0);
  };

  // Filter Logic
  const filteredList = list.filter((item) => {
    const searchMatch =
      (item.name && item.name.toLowerCase().includes(filterSearch.toLowerCase())) ||
      (item.direzione_generale && item.direzione_generale.toLowerCase().includes(filterSearch.toLowerCase())) ||
      (item.pec && item.pec.toLowerCase().includes(filterSearch.toLowerCase()));

    const typeMatch = filterType ? item.type === filterType : true;

    return searchMatch && typeMatch;
  });

  // Sort Logic
  const sortedList = [...filteredList].sort((a, b) => {
    const valueA = a[orderBy] || '';
    const valueB = b[orderBy] || '';

    if (typeof valueA === 'string' && typeof valueB === 'string') {
      return order === 'asc'
        ? valueA.localeCompare(valueB)
        : valueB.localeCompare(valueA);
    } else {
      return order === 'asc' ? valueA - valueB : valueB - valueA;
    }
  });

  const uniqueTypes = [...new Set(list.map(item => item.type).filter(Boolean))];
  const activeFiltersCount = (filterType ? 1 : 0);

  const goToDetail = (item) => {
    navigate(`/issuer/${item.id}`, { state: { name: item.name } });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <IssuerLogoIcon size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Banche e Finanziarie
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Creditori bancari e finanziari con volumi e stato delle pratiche associate
            </Typography>
          </Box>
        </Box>
      </Box>

      <StyledCard>
        <Grid container spacing={2} sx={{ p: 2, alignItems: 'center' }}>
          <Grid item xs={12} md={showFilters ? 8 : 12}>
            <TextField
              fullWidth
              placeholder="Cerca per nome, direzione generale, PEC..."
              value={filterSearch}
              onChange={handleFilterSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={20} color="#637381" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {filterSearch && (
                      <IconButton size="small" onClick={() => setFilterSearch('')}>
                        <X size={20} />
                      </IconButton>
                    )}
                    <IconButton onClick={() => setShowFilters(!showFilters)}>
                      <Badge badgeContent={activeFiltersCount} color="primary">
                        <FilterAltIcon color={showFilters || activeFiltersCount > 0 ? 'primary' : 'action'} />
                      </Badge>
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          
          {showFilters && (
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={filterType}
                  label="Tipo"
                  onChange={handleFilterType}
                >
                  <MenuItem value=""><em>Tutti</em></MenuItem>
                  {uniqueTypes.map((option) => (
                    <MenuItem key={option} value={option}>{option}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'id'}
                    direction={orderBy === 'id' ? order : 'asc'}
                    onClick={() => handleRequestSort('id')}
                  >
                    ID
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'name'}
                    direction={orderBy === 'name' ? order : 'asc'}
                    onClick={() => handleRequestSort('name')}
                  >
                    Nome
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'type'}
                    direction={orderBy === 'type' ? order : 'asc'}
                    onClick={() => handleRequestSort('type')}
                  >
                    Tipo
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'direzione_generale'}
                    direction={orderBy === 'direzione_generale' ? order : 'asc'}
                    onClick={() => handleRequestSort('direzione_generale')}
                  >
                    Direzione Generale
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'pec'}
                    direction={orderBy === 'pec' ? order : 'asc'}
                    onClick={() => handleRequestSort('pec')}
                  >
                    PEC
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === 'total_negotiations'}
                    direction={orderBy === 'total_negotiations' ? order : 'asc'}
                    onClick={() => handleRequestSort('total_negotiations')}
                  >
                    Totale Pratiche
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  % sul totale
                </TableCell>
                <TableCell align="center">Dettaglio</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : sortedList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                      Nessun creditore trovato
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedList
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((item) => {
                    const total = item.total_negotiations || 0;
                    const percentage =
                      totalNegotiations > 0
                        ? (total * 100) / totalNegotiations
                        : 0;
                    return (
                      <TableRow key={item.id} hover>
                        <TableCell>{item.id}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {item.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={item.type} 
                            size="small" 
                            variant="outlined" 
                          />
                        </TableCell>
                        <TableCell>{item.direzione_generale || '-'}</TableCell>
                        <TableCell>{item.pec || '-'}</TableCell>
                        <TableCell align="right">
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 28,
                              height: 28,
                              borderRadius: 0,
                              bgcolor: 'rgba(255, 171, 0, 0.08)',
                              color: '#FF9800',
                              boxShadow: '0 0 0 1px rgba(255, 171, 0, 0.24)',
                              fontWeight: 'bold',
                              fontSize: '0.8rem',
                            }}
                          >
                            {total}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {totalNegotiations > 0 ? (
                            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                              <CircularProgress
                                variant="determinate"
                                value={100}
                                size={36}
                                thickness={6}
                                sx={{ color: 'rgba(145, 158, 171, 0.24)' }}
                              />
                              <CircularProgress
                                variant="determinate"
                                value={percentage}
                                size={36}
                                thickness={6}
                                sx={{ position: 'absolute', left: 0 }}
                                color="warning"
                              />
                              <Box
                                sx={{
                                  top: 0,
                                  left: 0,
                                  bottom: 0,
                                  right: 0,
                                  position: 'absolute',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                  {`${percentage.toFixed(2)}%`}
                                </Typography>
                              </Box>
                            </Box>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton color="primary" onClick={() => goToDetail(item)}>
                            <Eye size={18} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredList.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
        />
      </StyledCard>
    </Box>
  );
}
